import { useEffect } from "react";
import type { URLOpenListenerEvent } from "@capacitor/app";
// eslint-disable-next-line no-duplicate-imports
import { App } from "@capacitor/app";
import { useNavigate } from "@tanstack/react-router";
import OneSignal from "onesignal-cordova-plugin";
import { Capacitor } from "@capacitor/core";
import { appNavigationLog } from "@/utils/appNavigationLogger";

/**
 * Helper function to check if URL is a deep link (https://ltcamobile or https://ltcapp)
 */
function isDeepLink(url: string): boolean {
	try {
		const urlObject = new URL(url);
		return (
			urlObject.protocol === "https:" &&
			(urlObject.hostname.startsWith("ltcamobile") ||
				urlObject.hostname.startsWith("ltcapp"))
		);
	} catch {
		return false;
	}
}

/**
 * Helper function to check if URL is a custom scheme (akademialtca://)
 */
function isCustomScheme(url: string): boolean {
	return url.startsWith("akademialtca://");
}

/**
 * Parse route string with optional query parameters
 * Separates pathname from query string and converts query params to object
 * @param route - Route string (e.g., "/workshops/webinar?viewStatus=RECORDED")
 * @returns Object with pathname and optional search params
 */
function parseRouteWithQueryParameters(
	route: string
): { pathname: string; search?: Record<string, string> } {
	try {
		// Check if route contains query string
		const queryIndex = route.indexOf("?");
		if (queryIndex === -1) {
			// No query string, return pathname only
			return { pathname: route };
		}

		// Split pathname and query string
		const pathname = route.substring(0, queryIndex);
		const queryString = route.substring(queryIndex + 1);

		// Parse query string to object
		const searchParameters = new URLSearchParams(queryString);
		const search: Record<string, string> = {};

		// Convert URLSearchParams to plain object
		searchParameters.forEach((value, key) => {
			search[key] = value;
		});

		// Return pathname with search params if any exist
		return Object.keys(search).length > 0
			? { pathname, search }
			: { pathname };
	} catch (error) {
		// If parsing fails, log warning and return route as-is (backward compatibility)
		appNavigationLog.warning(
			"Navigation",
			"Failed to parse route with query params, using route as-is",
			{ route, error: error instanceof Error ? error.message : String(error) },
			"internal"
		);
		return { pathname: route };
	}
}

/**
 * Extract route path from URL
 * For deep links: extracts pathname with query string
 * For custom schemes: extracts everything after "://" including query string
 */
function extractRouteFromUrl(url: string): string | null {
	try {
		if (isDeepLink(url)) {
			const urlObject = new URL(url);
			// Return pathname with query string if present
			const route = urlObject.pathname + urlObject.search;
			return route || null;
		} else if (isCustomScheme(url)) {
			// Extract path from custom scheme: akademialtca://path?query -> /path?query
			const match = url.match(/akademialtca:\/\/(.+)/);
			if (match && match[1]) {
				// Ensure it starts with / and preserve query string if present
				const path = match[1].startsWith("/") ? match[1] : `/${match[1]}`;
				return path;
			}
			return null;
		}
		return null;
	} catch {
		return null;
	}
}

/**
 * Safe navigation handler with error handling
 */
async function handleNavigation(
	route: string,
	navigate: ReturnType<typeof useNavigate>,
	source?: "deep-link" | "onesignal" | "internal"
): Promise<void> {
	try {
		if (!route) {
			appNavigationLog.warning(
				"Navigation",
				"Empty route provided, skipping navigation",
				{ source },
				source
			);
			return;
		}

		// Parse route to separate pathname and search params
		const { pathname, search } = parseRouteWithQueryParameters(route);

		// Log parsed values for debugging
		appNavigationLog.info(
			"Navigation",
			`Navigating to: ${pathname}${search ? ` with search params: ${JSON.stringify(search)}` : ""}`,
			{ route, pathname, search, source },
			source,
			route
		);

		// Navigate with search params if they exist, otherwise use pathname only
		if (search && Object.keys(search).length > 0) {
			await navigate({ to: pathname, search });
		} else {
			await navigate({ to: pathname });
		}

		appNavigationLog.success(
			"Navigation",
			`Successfully navigated to: ${pathname}${search ? ` with search params` : ""}`,
			{ route, pathname, search, source },
			source,
			route
		);
	} catch (error) {
		appNavigationLog.error(
			"Navigation",
			`Error navigating to ${route}`,
			{ route, source, error: error instanceof Error ? error.message : String(error) },
			source,
			route
		);

		// Fallback to home page on error
		try {
			appNavigationLog.info(
				"Navigation",
				"Attempting fallback navigation to home",
				{ originalRoute: route, source },
				source
			);
			await navigate({ to: "/" });
			appNavigationLog.success(
				"Navigation",
				"Fallback navigation to home successful",
				{ originalRoute: route, source },
				source,
				"/"
			);
		} catch (fallbackError) {
			appNavigationLog.error(
				"Navigation",
				"Fallback navigation failed",
				{
					originalRoute: route,
					source,
					error:
						fallbackError instanceof Error
							? fallbackError.message
							: String(fallbackError),
				},
				source
			);
		}
	}
}

/**
 * Consolidated component for handling app navigation from:
 * - Deep links (appUrlOpen events from Capacitor)
 * - OneSignal push notifications (with priority-based routing)
 * - OneSignal In-App Messages (custom action IDs mapped to routes)
 *
 * Priority order for OneSignal notifications:
 * 1. Launch URL - Deep Link (https://ltcamobile, https://ltcapp)
 * 2. Launch URL - Custom scheme (akademialtca://)
 * 3. additionalData.route
 * 
 * Only one option is executed according to priority.
 */
const AppNavigationListener = (): null => {
	const navigate = useNavigate();

	useEffect(() => {
		appNavigationLog.info(
			"Initialization",
			"AppNavigationListener initialized",
			{ platform: Capacitor.getPlatform() },
			"internal"
		);

		// ========================================================================
		// DEEP LINKS HANDLING (Capacitor App URL Open)
		// ========================================================================
		const deepLinkHandlerPromise = App.addListener(
			"appUrlOpen",
			(event: URLOpenListenerEvent) => {
				appNavigationLog.info(
					"Deep Link",
					"appUrlOpen event received",
					{ url: event.url },
					"deep-link"
				);

				const route = extractRouteFromUrl(event.url);

				if (route) {
					appNavigationLog.info(
						"Deep Link",
						`Extracted route from deep link: ${route}`,
						{ originalUrl: event.url, extractedRoute: route },
						"deep-link",
						route
					);

					handleNavigation(route, navigate, "deep-link").catch((error) => {
						appNavigationLog.error(
							"Deep Link",
							"Error handling deep link",
							{
								url: event.url,
								route,
								error: error instanceof Error ? error.message : String(error),
							},
							"deep-link",
							route
						);
					});
				} else {
					appNavigationLog.warning(
						"Deep Link",
						"Could not extract route from deep link URL",
						{ url: event.url },
						"deep-link"
					);
				}
			}
		)
			.then(() => {
				appNavigationLog.success(
					"Initialization",
					"Deep link listener registered successfully",
					{},
					"internal"
				);
			})
			.catch((error) => {
				appNavigationLog.error(
					"Initialization",
					"Error adding appUrlOpen listener",
					{ error: error instanceof Error ? error.message : String(error) },
					"internal"
				);
				return null;
			});

		// ========================================================================
		// ONESIGNAL NOTIFICATIONS HANDLING
		// ========================================================================
		if (
			Capacitor.isNativePlatform() &&
			import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG === "true"
		) {
			try {
				appNavigationLog.info(
					"Initialization",
					"Setting up OneSignal notification listener",
					{},
					"internal"
				);

				// OneSignal addEventListener may return void, so we don't store the listener
				// The listener will be active for the lifetime of the component
				OneSignal.Notifications.addEventListener("click", (event) => {
					appNavigationLog.info(
						"OneSignal",
						"OneSignal notification clicked",
						{ notification: event.notification },
						"onesignal"
					);

					const notification = event.notification;
					const launchUrl =
						(notification as { launchURL?: string; launchUrl?: string })
							.launchURL ||
						(notification as { launchURL?: string; launchUrl?: string })
							.launchUrl;
					const additionalData = notification.additionalData as
						| Record<string, unknown>
						| undefined;

					appNavigationLog.info(
						"OneSignal",
						"Processing notification data",
						{
							hasLaunchUrl: !!launchUrl,
							launchUrl,
							hasAdditionalData: !!additionalData,
							additionalData,
						},
						"onesignal"
					);

					let routeToNavigate: string | null = null;
					let priority: number | null = null;

					// Priority 1: Launch URL - Deep Link (https://ltcamobile, https://ltcapp)
					if (launchUrl && isDeepLink(launchUrl)) {
						priority = 1;
						appNavigationLog.info(
							"OneSignal",
							"Priority 1: Deep Link detected",
							{ launchUrl },
							"onesignal"
						);
						routeToNavigate = extractRouteFromUrl(launchUrl);
					}
					// Priority 2: Launch URL - Custom scheme (akademialtca://)
					else if (launchUrl && isCustomScheme(launchUrl)) {
						priority = 2;
						appNavigationLog.info(
							"OneSignal",
							"Priority 2: Custom scheme detected",
							{ launchUrl },
							"onesignal"
						);
						routeToNavigate = extractRouteFromUrl(launchUrl);
					}
					// Priority 3: additionalData.route
					else if (
						additionalData?.["route"] &&
						typeof additionalData["route"] === "string"
					) {
						priority = 3;
						const route = additionalData["route"];
						appNavigationLog.info(
							"OneSignal",
							"Priority 3: additionalData.route found",
							{ route },
							"onesignal"
						);
						routeToNavigate = route;
					}

					// Execute navigation if route was determined
					if (routeToNavigate) {
						appNavigationLog.success(
							"OneSignal",
							`Route determined (Priority ${priority}): ${routeToNavigate}`,
							{ priority, route: routeToNavigate, launchUrl, additionalData },
							"onesignal",
							routeToNavigate
						);

						handleNavigation(routeToNavigate, navigate, "onesignal").catch(
							(error) => {
								appNavigationLog.error(
									"OneSignal",
									"Error handling OneSignal notification",
									{
										route: routeToNavigate,
										priority,
										error: error instanceof Error ? error.message : String(error),
									},
									"onesignal",
									routeToNavigate || undefined
								);
							}
						);
					} else {
						appNavigationLog.warning(
							"OneSignal",
							"No valid route found in notification, skipping navigation",
							{ launchUrl, additionalData },
							"onesignal"
						);
					}
				});

				OneSignal.InAppMessages.addEventListener("click", (event) => {
					const actionId = event.result.actionId;

					appNavigationLog.info(
						"OneSignal IAM",
						"In-App Message clicked",
						{
							actionId,
							messageId: event.message.messageId,
							url: event.result.url,
						},
						"onesignal"
					);

					const inAppActionRoutes: Record<string, string> = {
						navigate_user_profil: "/user-profil",
					};

					const route = actionId ? inAppActionRoutes[actionId] : null;

					if (route) {
						handleNavigation(route, navigate, "onesignal").catch((error) => {
							appNavigationLog.error(
								"OneSignal IAM",
								`Error navigating for IAM action: ${actionId}`,
								{
									actionId,
									route,
									error:
										error instanceof Error ? error.message : String(error),
								},
								"onesignal",
								route
							);
						});
					}
				});

				appNavigationLog.success(
					"Initialization",
					"OneSignal notification and In-App Message listeners registered successfully",
					{},
					"internal"
				);
			} catch (error) {
				appNavigationLog.error(
					"Initialization",
					"Error setting up OneSignal listener",
					{ error: error instanceof Error ? error.message : String(error) },
					"internal"
				);
			}
		} else {
			appNavigationLog.info(
				"Initialization",
				"OneSignal listener skipped (not native platform or feature flag disabled)",
				{
					isNativePlatform: Capacitor.isNativePlatform(),
					featureFlag: import.meta.env.VITE_ONE_SIGNAL_FEATURE_FLAG,
				},
				"internal"
			);
		}

		// Cleanup function
		return (): void => {
			appNavigationLog.info(
				"Cleanup",
				"Cleaning up AppNavigationListener",
				{},
				"internal"
			);

			deepLinkHandlerPromise
				.then((handler) => {
					if (
						handler &&
						typeof handler === "object" &&
						"remove" in handler
					) {
						const handlerWithRemove = handler as { remove: () => void | Promise<void> };
						try {
							const removeResult = handlerWithRemove.remove();
							if (removeResult && typeof removeResult === "object" && "then" in removeResult) {
								// It's a Promise, ignore it
								removeResult.catch(() => {
									// Ignore errors
								});
							}
						} catch {
							// Ignore errors
						}
						appNavigationLog.success(
							"Cleanup",
							"Deep link listener removed",
							{},
							"internal"
						);
					}
				})
				.catch((error: unknown) => {
					appNavigationLog.error(
						"Cleanup",
						"Error removing appUrlOpen listener",
						{ error: error instanceof Error ? error.message : String(error) },
						"internal"
					);
				});

			// Note: OneSignal listener cleanup is handled automatically by OneSignal SDK
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return null;
};

export default AppNavigationListener;

